import type { ApplicationDto, TenantDto } from '@avila-roffe/api-clients-admin-bff';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { AssetScope, AssetStatus, Skill } from '../types';

// ─── Schema ───────────────────────────────────────────────────────────────────

const baseSchema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  shortDescription: z.string().optional(),
  status: z.enum(['ACTIVE', 'DRAFT', 'DEPRECATED']),
  appSlug: z.string().min(1, 'Application is required'),
  scope: z.enum(['PUBLIC', 'APPLICATION', 'TENANT']),
  tenantSlug: z.string().optional().default(''),
  tags: z.array(z.string()).default([]),
  compatibleTools: z.array(z.string()).default([]),
});

const createSchema = baseSchema.extend({
  name: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
});

const editSchema = baseSchema;

export type SkillCreateFormData = z.infer<typeof createSchema>;
export type SkillEditFormData = z.infer<typeof editSchema>;
export type SkillFormData = SkillCreateFormData | SkillEditFormData;

// ─── Props ────────────────────────────────────────────────────────────────────

interface SkillFormBaseProps {
  isSuperAdmin: boolean;
  apps: ApplicationDto[];
  tenants: TenantDto[];
  submitting?: boolean;
  onCancel?: () => void;
  formId?: string;
  onDirtyChange?: (dirty: boolean) => void;
}

interface SkillFormCreateProps extends SkillFormBaseProps {
  mode: 'create';
  initialValue?: Partial<SkillCreateFormData>;
  onSubmit: (data: SkillCreateFormData) => Promise<void>;
}

interface SkillFormEditProps extends SkillFormBaseProps {
  mode: 'edit';
  initialValue: Skill;
  onSubmit: (data: SkillEditFormData) => Promise<void>;
}

type SkillFormProps = SkillFormCreateProps | SkillFormEditProps;

// ─── Tag chip input ───────────────────────────────────────────────────────────

function TagChipInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  function addTag(raw: string) {
    const tag = raw.trim().replace(/,+$/, '');
    if (tag && !value.includes(tag)) onChange([...value, tag]);
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="rounded-full hover:bg-muted-foreground/20"
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        placeholder={placeholder ?? 'Type and press Enter…'}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && e.currentTarget.value.trim()) {
            e.preventDefault();
            addTag(e.currentTarget.value);
            e.currentTarget.value = '';
          }
        }}
        onBlur={(e) => {
          if (e.currentTarget.value.trim()) {
            addTag(e.currentTarget.value);
            e.currentTarget.value = '';
          }
        }}
      />
    </div>
  );
}

// ─── SkillForm ────────────────────────────────────────────────────────────────

export function SkillForm(props: SkillFormProps) {
  const { mode, isSuperAdmin, apps, tenants, onSubmit, formId, onDirtyChange } = props;

  const defaultValues =
    mode === 'edit'
      ? {
          displayName: props.initialValue.interface.displayName || '',
          shortDescription: props.initialValue.interface.shortDescription || '',
          status: props.initialValue.status,
          appSlug: props.initialValue.appSlug,
          scope: props.initialValue.scope,
          tenantSlug: props.initialValue.tenantSlug ?? '',
          tags: props.initialValue.tags,
          compatibleTools: props.initialValue.compatibleTools,
        }
      : {
          displayName: props.initialValue?.displayName ?? '',
          shortDescription: props.initialValue?.shortDescription ?? '',
          status: (props.initialValue?.status ?? 'DRAFT') as AssetStatus,
          appSlug: props.initialValue?.appSlug ?? apps[0]?.slug ?? '',
          scope: (props.initialValue?.scope ?? 'APPLICATION') as AssetScope,
          tenantSlug: props.initialValue?.tenantSlug ?? '',
          tags: props.initialValue?.tags ?? [],
          compatibleTools: props.initialValue?.compatibleTools ?? [],
          name: props.initialValue?.name ?? '',
        };

  // biome-ignore lint/suspicious/noExplicitAny: create/edit schemas have different shapes; cast avoids verbose conditional types
  const form = useForm<any>({
    resolver: zodResolver(mode === 'create' ? createSchema : editSchema),
    defaultValues,
  });

  const {
    formState: { isDirty },
  } = form;
  const watchedScope = form.watch('scope');

  // biome-ignore lint/correctness/useExhaustiveDependencies: lift dirty state to parent
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  async function onValid(data: unknown) {
    await (onSubmit as (d: unknown) => Promise<void>)(data);
  }

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={(e) => void form.handleSubmit(onValid)(e)}
        className="space-y-4"
        noValidate
      >
        {/* Slug — create only */}
        {mode === 'create' && (
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Slug <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="my-skill" {...field} />
                </FormControl>
                <p className="text-[11px] text-muted-foreground">
                  Lowercase letters, numbers, and hyphens. Cannot be changed after creation.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Display Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="My Skill" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="shortDescription"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Description</FormLabel>
              <FormControl>
                <Textarea
                  rows={2}
                  placeholder="What this skill does in one sentence"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope</FormLabel>
                <Select
                  onValueChange={(v) => {
                    field.onChange(v);
                    if (v !== 'TENANT') form.setValue('tenantSlug', '');
                  }}
                  value={field.value}
                  disabled={!isSuperAdmin}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="APPLICATION">Application</SelectItem>
                    <SelectItem value="TENANT">Tenant</SelectItem>
                    <SelectItem value="PUBLIC">Public</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchedScope !== 'PUBLIC' && (
            <FormField
              control={form.control}
              name="appSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Application <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!isSuperAdmin && mode === 'edit'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select application" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {apps.map((app) => (
                        <SelectItem key={app.slug} value={app.slug}>
                          {app.displayName || app.name || app.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {watchedScope === 'TENANT' && (
            <FormField
              control={form.control}
              name="tenantSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tenant</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={t.slug} value={t.slug}>
                          {t.name || t.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagChipInput value={field.value ?? []} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="compatibleTools"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Compatible Tools</FormLabel>
              <FormControl>
                <TagChipInput
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder="Type a tool name and press Enter…"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
